import { useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";

import { logoutProcessor } from "./helpers/logoutProcessor";
import { playSuccess, playError } from "./helpers/audioPlayer";
import { ArrowLeft, CheckCircle } from "lucide-react";
import { toast } from "react-toastify";
import { useTranslation } from "../i18n";
import HiddenScanInput from "./HiddenScanInput";
import FlowLoader from "./FlowLoader";
import ScanStatusIcons from "./ScanStatusIcons";
import ScanFlashOverlay from "./ScanFlashOverlay";
import FitWord from "./FitWord";
import { conveyorLogger } from "./helpers/logHandler";
import { apiFetch } from "../api/apiFetch";

type ConveyorHoldingPalletsProps = {
    goBack: () => void;
};

const ConveyorHoldingPallets = ({ goBack }: ConveyorHoldingPalletsProps) => {
    const navigator = useNavigate();
    const { t } = useTranslation();

    const [step, setStep] = useState<number>(1);
    const [scan, setScan] = useState<string>("");
    const [palletId, setPalletId] = useState<string>("");
    const [destination, setDestination] = useState<string[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [scanStatus, set_scanStatus] = useState<"idle" | "success" | "error">("idle");
    const statusTimerRef = useRef<NodeJS.Timeout | null>(null);

    const flashScanStatus = (status: "success" | "error") => {
        if (statusTimerRef.current) clearTimeout(statusTimerRef.current);
        set_scanStatus(status);
        statusTimerRef.current = setTimeout(() => set_scanStatus("idle"), 500);
    };

    const doScanPallet = useCallback(async (scannedValue?: string) => {
        const val = scannedValue ?? scan;
        if (val.trim() === "") return;

        const scannedPalletId = val.trim();
        setPalletId(scannedPalletId);
        setScan("");
        setStep(2);
        setLoading(true);

        const warehouse = scannedPalletId.startsWith("UK") ? "Newport" : scannedPalletId.startsWith("EU") ? "Poland" : "Test";

        await apiFetch(`/conveyor/holdingpallets/get`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json"
            },
            body: JSON.stringify({
                warehouse: warehouse,
                palletId: scannedPalletId
            })
        }).then(async (res) => {
            if (res.ok) {
                const data = await res.json();
                setDestination(Array.isArray(data) ? data : [data]);
                setLoading(false);
                conveyorLogger(warehouse, "Scanned Holding Pallet", scannedPalletId, "Warehouse", "Hand", "success");
            } else if (res.status === 401) {
                logoutProcessor();
                navigator("/login");
            } else {
                const data = await res.json();
                console.log(data);
                flashScanStatus("error");
                playError();
                toast.dismiss();
                console.log(data);
                console.log(typeof data);
                if (data.getLocationResult.response == "No free zones left") {
                    toast.error(t("pallet_no_zones_left"));
                } else {
                    toast.error(t("pallet_details_failed"));
                }
                setLoading(false);
                setStep(1);
                setPalletId("");
                conveyorLogger(warehouse, "Scanned Holding Pallet", scannedPalletId, "Warehouse", "Hand", "error");
            }
        }).catch(() => {
            flashScanStatus("error");
            playError();
            toast.dismiss(); toast.error(t("pallet_details_failed"));
            setLoading(false);
            setStep(1);
            setPalletId("");
            conveyorLogger(warehouse, "Scanned Holding Pallet", scannedPalletId, "Warehouse", "Hand", "error");
        });
    }, [scan, navigator, t]);

    const doScanLocation = useCallback(async (scannedValue?: string) => {
        let val = scannedValue ?? scan;
        val = val.trim();
        if (val === "") return;

        console.log(val);
        console.log(destination);
        console.log(destination.includes(val));

        if (!destination.includes(val)) {
            playError();
            toast.dismiss(); toast.error(t("wrong_location"));
            setScan("");
            conveyorLogger("Newport", "Put Holding Pallet In Zone", palletId, "Hand", val, "wrong scan");
            return;
        }

        setScan("");
        setSubmitting(true);

        const warehouse = palletId.startsWith("UK") ? "Newport" : palletId.startsWith("EU") ? "Poland" : "Test";
        const user = window.localStorage.getItem("user") ? JSON.parse(window.localStorage.getItem("user")!).name : "Unknown";

        await apiFetch(`/conveyor/holdingpallets/collect`, {
            method: "PATCH",
            headers: {
                "Content-Type": "application/json"
            },
            body: JSON.stringify({
                warehouse: warehouse,
                palletId: palletId,
                location: val,
                username: user
            })
        }).then(async (res) => {
            if (res.ok) {
                flashScanStatus("success");
                playSuccess();
                setStep(1);
                setPalletId("");
                setDestination([]);
                conveyorLogger(warehouse, "Put Holding Pallet In Zone", palletId, "Hand", val, "success");
            } else if (res.status === 401) {
                logoutProcessor();
                navigator("/login");
            } else {
                console.log(res.json());
                flashScanStatus("error");
                playError();
                toast.dismiss(); toast.error(t("pallet_collect_failed"));
                conveyorLogger(warehouse, "Put Holding Pallet In Zone", palletId, "Hand", val, "error");
            }
        }).catch(() => {
            flashScanStatus("error");
            playError();
            toast.dismiss(); toast.error(t("pallet_collect_failed"));
            conveyorLogger(warehouse, "Put Holding Pallet In Zone", palletId, "Hand", val, "error");
        });

        setSubmitting(false);
    }, [scan, destination, palletId, navigator, t]);

    const handleSubmit = useCallback((scannedValue?: string) => {
        if (step === 1) {
            doScanPallet(scannedValue);
        } else {
            doScanLocation(scannedValue);
        }
    }, [step, doScanPallet, doScanLocation]);

    return (
        <div className="scan-panel" style={{ backgroundColor: scanStatus === "success" ? "rgba(34, 197, 94, 0.18)" : undefined }}>
            <ScanFlashOverlay active={scanStatus === "error"} />
            <div className="page-title-bar">
                <h1 className="page-title">{t("holding_pallets")}</h1>
                <button className="page-back-btn" onClick={goBack}><ArrowLeft size={20} /></button>
            </div>

            <div className="scan-centre">
                {step === 1 && <ScanStatusIcons status={scanStatus === "error" ? "idle" : scanStatus} />}

                {step === 1 && (
                    <div className="conveyor-scan-group">
                        <div className={`scan-command ${scanStatus !== "idle" ? "scan-command-hidden" : ""}`}>
                            {t("scan_pallet_id").toUpperCase().split(" ").map((word, i) => (
                                <FitWord key={i}>{word}</FitWord>
                            ))}
                        </div>
                    </div>
                )}

                {step === 2 && (
                    <div className="conveyor-scan-group">
                        {loading ? (
                            <FlowLoader />
                        ) : (
                            <>
                                <div className={`scan-command ${scanStatus === "error" ? "scan-command-hidden" : ""}`}>
                                    {t("take_to_location", { location: destination[destination.length - 1] }).toUpperCase().split(" ").map((word, i) => (
                                        <FitWord key={i}>{word}</FitWord>
                                    ))}
                                </div>
                                <div className={`scan-feedback-area ${scanStatus === "error" ? "scan-command-hidden" : ""}`}>
                                    <p className="conveyor-scan-subtitle">{t("pallet_label", { id: palletId })}</p>
                                    <p className="conveyor-scan-subtitle-text">{t("scan_location_confirm")}</p>
                                </div>
                                {submitting && <p className="conveyor-status-text">{t("submitting")}</p>}
                            </>
                        )}
                    </div>
                )}
            </div>
            {step === 2 && scanStatus === "success" && (
                <div className="s2-inline-status s2-inline-success"><CheckCircle size={140} strokeWidth={3} /></div>
            )}
            <HiddenScanInput
                value={scan}
                onChange={setScan}
                onSubmit={handleSubmit}
                disabled={submitting}
            />
        </div>
    );
};

export default ConveyorHoldingPallets;
